/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * This software and its documentation are proprietary to Sila Artificial
 * Intelligence Research LLC. Unauthorized copying, distribution, or use
 * of this software is strictly prohibited.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

interface TooltipPayload {
  name: string;
  value: number | string;
  color?: string;
  payload?: any;
}

interface GlassTooltipProps {
  active?: boolean;
  payload?: TooltipPayload[];
  label?: string;
  formatter?: (value: number | string, name: string) => string;
  labelFormatter?: (label: string) => string;
  valuePrefix?: string;
  valueSuffix?: string;
}

/**
 * GlassTooltip - Glass morphism tooltip for Recharts
 * Provides consistent, theme-aware tooltips for all charts
 */
const GlassTooltip: React.FC<GlassTooltipProps> = ({
  active,
  payload,
  label,
  formatter,
  labelFormatter,
  valuePrefix = '',
  valueSuffix = '',
}) => {
  const { isDark } = useTheme();

  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const formattedLabel = labelFormatter ? labelFormatter(label || '') : label;

  return (
    <div
      className="glass-tooltip"
      style={{
        backgroundColor: isDark
          ? 'rgba(26, 26, 46, 0.95)'
          : 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        borderRadius: '8px',
        boxShadow: isDark
          ? '0 4px 12px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(59, 130, 246, 0.2)'
          : '0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)',
        padding: '12px 16px',
        minWidth: '140px',
      }}
    >
      {formattedLabel && (
        <p
          className="text-sm font-medium mb-2"
          style={{
            color: isDark ? '#F9FAFB' : '#1F2937',
            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
            paddingBottom: '8px',
          }}
        >
          {formattedLabel}
        </p>
      )}
      <div className="space-y-1.5">
        {payload.map((entry, index) => {
          const displayValue = formatter
            ? formatter(entry.value, entry.name)
            : `${valuePrefix}${entry.value}${valueSuffix}`;

          return (
            <div key={index} className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <div
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: entry.color || '#3B82F6' }}
                />
                <span
                  className="text-sm"
                  style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}
                >
                  {entry.name}
                </span>
              </div>
              <span
                className="text-sm font-semibold"
                style={{ color: isDark ? '#F9FAFB' : '#1F2937' }}
              >
                {displayValue}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Get tooltip style props for Recharts
 */
export const getTooltipStyle = (isDark: boolean) => ({
  contentStyle: {
    backgroundColor: isDark ? 'rgba(26, 26, 46, 0.95)' : 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    border: `1px solid ${isDark ? 'rgba(59, 130, 246, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
    borderRadius: '8px',
    boxShadow: isDark
      ? '0 4px 12px rgba(0, 0, 0, 0.4)'
      : '0 4px 12px rgba(0, 0, 0, 0.15)',
    padding: '12px',
  },
  labelStyle: {
    color: isDark ? '#F9FAFB' : '#1F2937',
    fontWeight: 600,
    marginBottom: '8px',
  },
  itemStyle: {
    color: isDark ? '#9CA3AF' : '#6B7280',
    fontSize: '14px',
  },
});

export default GlassTooltip;
